import React, { useCallback, useEffect, useId, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { KeyboardArrowDown, KeyboardArrowRight } from '@mui/icons-material'
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import { SxProps, Theme, alpha } from '@mui/material/styles'
import { SystemStyleObject } from '@mui/system'

import { FileUploadResponse, BlueprintApiItem, useGetGamesQuery, useGetTrackedGamesQuery } from '../../../../api/core'
import { SortLabel } from '../../../../components/GRTable/HeaderCell/SortLabel/SortLabel'
import { SortOrder } from '../../../../components/GRTable/types/SortOrder'
import { GRTooltip } from '../../../../components/GRTooltip/GRTooltip'
import { useCurrentUserLanguage } from '../../../account/hooks/useCurrentUserLanguage'
import { AppType, GamePlatform } from '../../../game/types/Game'
import { useLockedGameDialog } from '../../../live-events/hooks/useLockedGameDialog'
import { TrackedGame } from '../../../live-events/types/TrackedGame'
import { useBlueprintDetails, useRetryBlueprintDetails } from '../../hooks/useBlueprintDetails'
import { useIsNarrowerThan } from '../../hooks/useIsNarrowerThan'
import { BlueprintEventsFilterPills } from '../BlueprintEventsFilterPills/BlueprintEventsFilterPills'
import { HIERARCHY_BAR_STICKY_HEIGHT, HIERARCHY_BAR_STICKY_TOP } from '../BlueprintHierarchyBar/BlueprintHierarchyBar'
import {
  DrilldownGameRows,
  DrilldownRowToggle,
  GameGroup,
  inlineTextButtonSx,
  isGameGroupDiscontinued,
  isNestedControlClick,
} from '../DrilldownGameRows/DrilldownGameRows'
import {
  BlueprintEventFilter,
  BlueprintEventSort,
  BlueprintEventSortKey,
  DEFAULT_BLUEPRINT_EVENT_SORT,
  blueprintSpanMatchesFilter,
  blueprintYearRange,
  gameGroupFilterMatchState,
  nextBlueprintEventSort,
} from '../helpers/blueprintEventRows'
import {
  DRILLDOWN_CELL_CLASS,
  DRILLDOWN_COLUMNS,
  DRILLDOWN_COLUMN_COUNT,
  DRILLDOWN_TABLE_SX,
  DrilldownColumnKey,
  EVENT_NAME_INDENT,
  GAME_ICON_RADIUS,
  GAME_ICON_SIZE,
  GAME_ROW_HEIGHT,
  SECTION_ROW_HEIGHT,
  DRILLDOWN_TABLE_MIN_WIDTH,
  TABLE_GUTTER,
  drilldownStickyHeaderSx,
  gameDividerColor,
} from '../helpers/drilldownTableLayout'
import { gameLevelBlueprintDisplayName, gameLevelBlueprintHasDisplayFields } from '../helpers/gameBlueprintDisplay'
import { getTranslatedName } from '../helpers/metricUtils'
import { compactToggleGroupSx } from '../helpers/toggleStyles'

interface BlueprintGamesDrilldownTableProps {
  blueprintId: string
  blueprintName: string
  // Event type (tag) id of the market blueprint — `m_liveEventTypeId` for the Live
  // Events calendar modal links (the page's leading event type).
  eventTypeId: string
  gameBlueprints?: BlueprintApiItem[]
  // Resolved conventional subgenre scope from the page filters; games outside
  // the scope are hidden client-side (mirrors the catalogue's segment model).
  effectiveSubgenreIds: string[]
  isLoading?: boolean
  // The game-level blueprints request failed; with nothing to show, the table offers `onRetry`.
  isError?: boolean
  onRetry?: () => void
  // The family's PC/console game-level blueprints, listed on their own tab. The tab
  // only appears once there is at least one.
  pcConsoleBlueprints?: BlueprintApiItem[]
}

type PlatformTab = 'mobile' | 'pcConsole'

const PLATFORM_TABS: PlatformTab[] = ['mobile', 'pcConsole']

const PLATFORM_TAB_LABEL_KEYS: Record<PlatformTab, string> = {
  mobile: 'market-analytics:drilldown_platform_mobile',
  pcConsole: 'market-analytics:drilldown_platform_pc_console',
}

// `compactToggleGroupSx` plus a right padding equal to the count chip's top/bottom inset
// (28px button, 18px chip), so the chip sits concentric with the pill's border.
const platformToggleGroupSx: SxProps<Theme> = [
  compactToggleGroupSx as SystemStyleObject<Theme>,
  { justifySelf: { md: 'center' }, '& .MuiToggleButton-root': { pr: '5px' } },
]

const countGames = (items: BlueprintApiItem[]) => new Set(items.map((item) => item.gameId ?? item.id)).size

// Name / company / platform / icon info resolved for a game, merged from the
// mobile games endpoint and (as a fallback) the tracked-games list.
type ResolvedGameInfo = { name: string; artist: string; appType?: AppType; platforms?: GamePlatform[]; iconUrl?: string }

// Prefer the resolved Game icon: the blueprint payload's `gameIcon.url` points
// at a fixed production host that other environments can't authorize.
function gameIconUrlOf(game: { getIcon?: () => string | FileUploadResponse | undefined }): string | undefined {
  const raw = typeof game.getIcon === 'function' ? game.getIcon() : undefined
  if (!raw) return undefined
  return typeof raw === 'string' ? raw : raw.url
}

// Resolve a display name from a (possibly market-ISO-keyed) name map — PC/Console
// blueprints carry `gameNames` keyed by market ISO, so fall back to any value.
const getGameDisplayName = gameLevelBlueprintDisplayName

// Header label key + (optional) sort key and tooltip key per grid column.
const COLUMN_HEADERS: Record<DrilldownColumnKey, { labelKey: string; sortKey?: BlueprintEventSortKey; tooltipKey?: string }> = {
  rank: { labelKey: 'market-analytics:event_table_col_rank', tooltipKey: 'market-analytics:event_table_col_rank_tooltip' },
  game: { labelKey: 'market-analytics:event_table_col_game_event' },
  start: { labelKey: 'market-analytics:event_table_col_start', sortKey: 'startDate' },
  end: { labelKey: 'market-analytics:event_table_col_end', sortKey: 'endDate' },
  duration: { labelKey: 'market-analytics:event_table_col_duration', sortKey: 'durationDays' },
  gap: { labelKey: 'market-analytics:event_table_col_gap', sortKey: 'gapBeforeDays' },
  status: { labelKey: 'market-analytics:event_table_col_status', sortKey: 'status' },
  actions: { labelKey: 'market-analytics:event_table_col_reskin_links' },
}

const ROW_SKELETON_COUNT = 6

type DrilldownSectionId = 'active' | 'discontinued'

// Active always leads; discontinued games follow the ones still running the family.
const DRILLDOWN_SECTION_ORDER: DrilldownSectionId[] = ['active', 'discontinued']

/**
 * Band separating the adopters still running the family from the games that have
 * been discontinued. Mirrors the tracked-game picker's sections
 * (`GameSearchResultsTable` + `SectionHeaderRow`): status dot, overline label,
 * count, and a chevron that folds the band away.
 */
const DrilldownSectionHeaderRow: React.FC<{
  sectionId: DrilldownSectionId
  count: number
  collapsed: boolean
  // Space-separated ids of the game rows in the band.
  controls: string
  onToggle: (sectionId: DrilldownSectionId) => void
}> = ({ sectionId, count, collapsed, controls, onToggle }) => {
  const { t } = useTranslation()
  const isDiscontinued = sectionId === 'discontinued'
  const toggle = useCallback(() => onToggle(sectionId), [onToggle, sectionId])
  const label = isDiscontinued ? t('live-events:tracking_section_discontinued_games') : t('live-events:tracking_section_active_games')

  return (
    <TableRow
      data-testid="drilldown-section-row"
      onClick={(event) => {
        if (!isNestedControlClick(event)) toggle()
      }}
      sx={(theme) => ({
        height: SECTION_ROW_HEIGHT,
        cursor: 'pointer',
        bgcolor: alpha(theme.palette.text.primary, 0.03),
        '& > td': { borderBottomColor: gameDividerColor(theme) },
        '&:hover': { bgcolor: alpha(theme.palette.text.primary, 0.05) },
      })}
    >
      {/* Both gutter hooks so the band's content lines up with the rank column on
          the left and the link rail on the right. */}
      <TableCell colSpan={DRILLDOWN_COLUMN_COUNT} className={`${DRILLDOWN_CELL_CLASS.rank} ${DRILLDOWN_CELL_CLASS.actions}`}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: isDiscontinued ? 'warning.main' : 'success.main' }} />
            {/* The band splits games by tracking status, while the blueprint rows inside carry
                their own "Discontinued" chip for adoption — the tooltip keeps the two apart. */}
            <GRTooltip
              content={isDiscontinued ? t('live-events:tracking_section_discontinued_games_tooltip') : t('live-events:tracking_section_active_games_tooltip')}
              styles={{ cursor: 'help', display: 'inline-flex' }}
            >
              <Typography variant="overline" sx={{ fontWeight: 'bold', lineHeight: 1 }} color="text.secondary">
                {label}
              </Typography>
            </GRTooltip>
            <Typography variant="overline" sx={{ lineHeight: 1 }} color="text.disabled">
              ({count})
            </Typography>
          </Stack>
          <DrilldownRowToggle expanded={!collapsed} controls={controls} label={label} onToggle={toggle}>
            {collapsed ? <KeyboardArrowRight fontSize="small" /> : <KeyboardArrowDown fontSize="small" />}
          </DrilldownRowToggle>
        </Stack>
      </TableCell>
    </TableRow>
  )
}

/**
 * Loading rows for the adopters tree table, shaped to a collapsed game row
 * (chevron + rank, 36px icon, name + publisher lines) so each placeholder
 * occupies the same 60px the real row does. Shared with the page-level
 * skeleton (`MarketLevelBlueprintDetailPageSkeleton`) so the two can't drift.
 */
export const BlueprintGamesDrilldownRowsSkeleton: React.FC<{ rowCount?: number }> = ({ rowCount = ROW_SKELETON_COUNT }) => (
  <Stack divider={<Divider flexItem />} spacing={0} aria-busy="true">
    {Array.from({ length: rowCount }, (_, idx) => (
      <Stack key={idx} direction="row" alignItems="center" sx={{ pl: `${TABLE_GUTTER}px`, pr: `${TABLE_GUTTER}px`, height: GAME_ROW_HEIGHT }}>
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ width: (DRILLDOWN_COLUMNS[0].width ?? 0) - TABLE_GUTTER, flexShrink: 0 }}>
          <Skeleton variant="circular" width={20} height={20} />
          <Skeleton variant="text" width={20} sx={{ fontSize: '0.75rem' }} />
        </Stack>
        <Skeleton variant="rounded" width={GAME_ICON_SIZE} height={GAME_ICON_SIZE} sx={{ borderRadius: GAME_ICON_RADIUS, flexShrink: 0 }} />
        <Box sx={{ flex: 1, minWidth: 0, pl: `${EVENT_NAME_INDENT - GAME_ICON_SIZE}px` }}>
          <Skeleton variant="text" width="35%" sx={{ fontSize: '0.875rem' }} />
          <Skeleton variant="text" width="50%" sx={{ fontSize: '0.75rem' }} />
        </Box>
      </Stack>
    ))}
  </Stack>
)

/**
 * "{blueprint} adopters" as one tree table: every adopting game is a row of a
 * shared column grid and expanding it inserts its event rows directly beneath
 * (`DrilldownGameRows`). Several games can be open at once; the header's
 * "Show" pills filter every open game's events and the column sort applies
 * within each open game. Event details are fetched lazily per expanded game.
 *
 * Games render in the backend's order — the blueprints list is pre-sorted
 * by US grossing rank (`srank` ascending, unranked last) as of GR-2916 — with
 * the actual `srank` shown in the rank column and the publisher (`artist`)
 * below the game name.
 */
const BlueprintGamesDrilldownTableComponent: React.FC<BlueprintGamesDrilldownTableProps> = ({
  blueprintId,
  blueprintName,
  eventTypeId,
  gameBlueprints,
  effectiveSubgenreIds,
  isLoading,
  isError,
  onRetry,
  pcConsoleBlueprints,
}) => {
  const { t } = useTranslation()
  const userLanguage = useCurrentUserLanguage()
  const gameRowIdPrefix = useId()
  const gameRowIdOf = useCallback((gameId: string) => `${gameRowIdPrefix}-${gameId}`, [gameRowIdPrefix])

  const [expandedGameIds, setExpandedGameIds] = useState<ReadonlySet<string>>(() => new Set())
  const toggleGame = useCallback((gameId: string) => {
    setExpandedGameIds((prev) => {
      const next = new Set(prev)
      if (next.has(gameId)) next.delete(gameId)
      else next.add(gameId)
      return next
    })
  }, [])

  // Both bands open at rest: the labels alone do the separating, and a retired
  // adopter's history is as much a part of the family's story as a live one.
  // Either band can still be folded away.
  const [collapsedSections, setCollapsedSections] = useState<ReadonlySet<DrilldownSectionId>>(() => new Set<DrilldownSectionId>())
  const toggleSection = useCallback((sectionId: DrilldownSectionId) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev)
      if (next.has(sectionId)) next.delete(sectionId)
      else next.add(sectionId)
      return next
    })
  }, [])

  const [filter, setFilter] = useState<BlueprintEventFilter>('all')
  const [sort, setSort] = useState<BlueprintEventSort>(DEFAULT_BLUEPRINT_EVENT_SORT)
  const handleSort = useCallback((key: BlueprintEventSortKey) => setSort((current) => nextBlueprintEventSort(current, key)), [])
  // Stable "now" per mount so statuses don't shift between renders (they only move across a day boundary).
  const now = useMemo(() => Date.now(), [])

  // The blueprints endpoint has no segment input, so the taxonomy filter is
  // applied client-side via each item's `conventionalSubgenreId` — mirrors the
  // backend's segment-adopting-games set so the list agrees with the adoption stats.
  const scopedMobileItems = useMemo(() => {
    if (!gameBlueprints) return []
    if (effectiveSubgenreIds.length === 0) return gameBlueprints
    const scopeSet = new Set(effectiveSubgenreIds)
    return gameBlueprints.filter((item) => item.conventionalSubgenreId != null && scopeSet.has(item.conventionalSubgenreId))
  }, [gameBlueprints, effectiveSubgenreIds])
  // PC/console games mostly have no (mobile) conventional subgenre, so the segment
  // filter would empty the tab; it lists every PC/console adopter instead.
  const pcConsoleItems = useMemo(() => pcConsoleBlueprints ?? [], [pcConsoleBlueprints])

  const mobileGameCount = useMemo(() => countGames(scopedMobileItems), [scopedMobileItems])
  const pcConsoleGameCount = useMemo(() => countGames(pcConsoleItems), [pcConsoleItems])
  const showPlatformTabs = pcConsoleGameCount > 0

  // `null` until the user picks a tab: then a family adopted only on PC/console opens
  // on that tab instead of an empty mobile list.
  const [selectedPlatformTab, setSelectedPlatformTab] = useState<PlatformTab | null>(null)
  useEffect(() => {
    setSelectedPlatformTab(null)
  }, [blueprintId])
  const platformTab: PlatformTab = !showPlatformTabs ? 'mobile' : selectedPlatformTab ?? (mobileGameCount === 0 && !isLoading ? 'pcConsole' : 'mobile')
  const isPcConsoleTab = platformTab === 'pcConsole'
  const handlePlatformTabChange = useCallback((_: React.MouseEvent<HTMLElement>, next: PlatformTab | null) => {
    if (next === null) return
    setSelectedPlatformTab(next)
    setFilter('all')
  }, [])

  const scopedItems = isPcConsoleTab ? pcConsoleItems : scopedMobileItems
  const platformGameCounts: Record<PlatformTab, number> = { mobile: mobileGameCount, pcConsole: pcConsoleGameCount }

  // Year pills come from the list payload's event span so they render before any game is expanded.
  // Built from every scoped item, not the filtered set, so picking a year can't remove the pills.
  const years = useMemo(() => blueprintYearRange(scopedItems, now), [scopedItems, now])
  // A year pill can disappear when the scope changes; fall back to "all".
  const effectiveFilter: BlueprintEventFilter = typeof filter === 'number' && !years.includes(filter) ? 'all' : filter

  // Collapsed games shouldn't stay open across filter changes — the list itself may shrink.
  useEffect(() => {
    setExpandedGameIds(new Set())
  }, [effectiveFilter, platformTab])

  // Drop the games a year filter provably excludes. Their events are only fetched on
  // expand, so without this the filter appeared to do nothing: the game stayed listed
  // with its full event count and only admitted it had nothing to show once opened.
  const filteredItems = useMemo(() => scopedItems.filter((item) => blueprintSpanMatchesFilter(item, effectiveFilter, now)), [scopedItems, effectiveFilter, now])

  // GR-2916 already ships artist / icons / sometimes names. Skip the extra
  // games/{ids} round-trip when every row can render from the list payload.
  const gameIds = useMemo(() => Array.from(new Set(scopedItems.map((item) => item.gameId).filter((id): id is string => Boolean(id)))), [scopedItems])
  const needsGameLookup = useMemo(() => scopedItems.some((item) => !gameLevelBlueprintHasDisplayFields(item, userLanguage)), [scopedItems, userLanguage])
  const { currentData: resolvedGames, isFetching: resolvedGamesFetching } = useGetGamesQuery(
    { ids: gameIds, userLanguage },
    { skip: !needsGameLookup || gameIds.length === 0 }
  )
  // Both retirement flags now ride on the blueprint rows themselves, so this is no
  // longer fetched to split the table. It still covers the PC/console name fallback
  // and the context the event-info popup needs.
  const { data: trackedGames } = useGetTrackedGamesQuery({ userLanguage })

  // TrackedGame per game id — the context (currencies, blueprint names) the hover
  // popup needs.
  const trackedGameById = useMemo(() => {
    const map = new Map<string, TrackedGame>()
    trackedGames?.forEach((tg) => {
      if (tg.game?.id) map.set(tg.game.id, tg)
    })
    return map
  }, [trackedGames])

  const resolvedById = useMemo(() => {
    const map = new Map<string, ResolvedGameInfo>()
    trackedGames?.forEach((tg) => {
      const game = tg.game
      if (game?.id) {
        map.set(game.id, {
          name: game.resolvedName ?? '',
          artist: game.artist ?? '',
          appType: game.appType,
          platforms: game.platforms,
          iconUrl: gameIconUrlOf(game),
        })
      }
    })
    resolvedGames?.forEach((game) => {
      if (!game?.id) return
      const existing = map.get(game.id)
      map.set(game.id, {
        name: game.resolvedName || existing?.name || '',
        artist: game.artist || existing?.artist || '',
        appType: game.appType ?? existing?.appType,
        platforms: game.platforms ?? existing?.platforms,
        iconUrl: gameIconUrlOf(game) ?? existing?.iconUrl,
      })
    })
    return map
  }, [resolvedGames, trackedGames])

  const { openLockedGameDialog, lockedGameDialog } = useLockedGameDialog()

  // Group the game-level blueprints by game, preserving the backend's order —
  // the list arrives pre-sorted by `srank` ascending with unranked games last
  // (GR-2916), which is exactly the mockup's grossing-rank ordering.
  const gameGroups = useMemo<GameGroup[]>(() => {
    const byGame = new Map<string, GameGroup>()
    for (const item of filteredItems) {
      const gameId = item.gameId ?? item.id
      const resolved = resolvedById.get(gameId)
      const existing = byGame.get(gameId)
      if (existing) {
        existing.eventsCount += item.liveEventsCount ?? 0
        existing.blueprints.push(item)
        if (!existing.artist && item.artist) existing.artist = item.artist
        if (existing.srank === undefined && item.srank !== undefined) existing.srank = item.srank
        if (item.gameAllowed === false) existing.isLocked = true
      } else {
        const iconUrl = (item.gameIcons && item.gameIcons.length > 0 ? item.gameIcons[0] : undefined) ?? resolved?.iconUrl ?? item.gameIcon?.url
        byGame.set(gameId, {
          gameId,
          name: resolved?.name || getGameDisplayName(item.gameNames, userLanguage),
          // Prefer the BE-shipped publisher (GR-2916); fall back to game-info resolution.
          artist: item.artist || resolved?.artist || '',
          iconUrl,
          // The PC/console list is classified by the diff, not a game lookup, so it
          // still gets its badge when the tracked-games list doesn't name the game.
          appType: isPcConsoleTab ? AppType.PC_CONSOLE : resolved?.appType,
          platforms: resolved?.platforms,
          eventsCount: item.liveEventsCount ?? 0,
          srank: item.srank,
          blueprints: [item],
          isLocked: item.gameAllowed === false,
        })
      }
    }
    const groups = Array.from(byGame.values())
    // Most events first within each game's blueprint list; game order stays as received.
    for (const group of groups) {
      group.blueprints.sort((a, b) => (b.liveEventsCount ?? 0) - (a.liveEventsCount ?? 0))
    }
    return groups
  }, [filteredItems, resolvedById, userLanguage, isPcConsoleTab])

  // Span alone is not enough for a year: scheduled instances can push `latestEvent`
  // forward while no started event falls in that year. Prefetch every blueprint's
  // events and drop games that resolve to zero matching rows. A locked game's events
  // come back empty, so it is judged by its span alone and never fetched.
  const filterMatchBlueprintIds = useMemo(() => filteredItems.filter((item) => item.gameAllowed !== false).map((item) => item.id), [filteredItems])
  const filterMatchEnabled = effectiveFilter !== 'all' && filterMatchBlueprintIds.length > 0
  const detailOptions = useMemo(() => ({ includePcConsole: isPcConsoleTab || undefined }), [isPcConsoleTab])
  const filterMatchDetails = useBlueprintDetails(blueprintId, filterMatchBlueprintIds, filterMatchEnabled, detailOptions)
  const detailByBlueprintId = useMemo(() => {
    const map = new Map<string, (typeof filterMatchDetails)[number]>()
    filterMatchDetails.forEach((entry) => map.set(entry.blueprintId, entry))
    return map
  }, [filterMatchDetails])
  // Games whose events failed to load stay listed (they can't be ruled out), so
  // the year's list may be off — say so rather than pass it off as exact.
  const failedFilterMatchIds = useMemo(
    () => (filterMatchEnabled ? filterMatchDetails.filter((entry) => entry.isError).map((entry) => entry.blueprintId) : []),
    [filterMatchEnabled, filterMatchDetails]
  )
  const retryBlueprintDetails = useRetryBlueprintDetails(blueprintId, detailOptions)
  const retryFailedFilterMatches = useCallback(() => retryBlueprintDetails(failedFilterMatchIds), [retryBlueprintDetails, failedFilterMatchIds])

  const blueprintDisplayName = useCallback(
    (names?: BlueprintApiItem['names']) => (names ? getTranslatedName(names, userLanguage) || Object.values(names).find(Boolean) || '' : ''),
    [userLanguage]
  )

  const visibleGameGroups = useMemo(() => {
    if (effectiveFilter === 'all') return gameGroups
    return gameGroups.filter(
      (group) => group.isLocked || gameGroupFilterMatchState(group.blueprints, detailByBlueprintId, effectiveFilter, blueprintDisplayName, now) !== 'no-match'
    )
  }, [gameGroups, effectiveFilter, detailByBlueprintId, blueprintDisplayName, now])

  // The list is all-time by design, so it names games that have since been
  // discontinued alongside the ones still running the family. Split them the way
  // the tracked-game picker does — active first, discontinued in their own band —
  // rather than interleaving them by rank, where a retired game reads as a live
  // adopter. Read off the rows' own `gameDiscontinued`; a game whose rows all omit
  // it is treated as active, since absence of the flag is not evidence of retirement.
  const sections = useMemo(() => {
    const active: GameGroup[] = []
    const discontinued: GameGroup[] = []
    for (const group of visibleGameGroups) {
      if (isGameGroupDiscontinued(group)) discontinued.push(group)
      else active.push(group)
    }
    return { active, discontinued }
  }, [visibleGameGroups])
  // One-sided lists need no bands — labelling every row "Active" says nothing.
  const showSections = sections.active.length > 0 && sections.discontinued.length > 0

  // Hold the skeleton until names resolve so rows never flash "Unknown game".
  const showSkeleton = Boolean(isLoading) || (needsGameLookup && gameIds.length > 0 && resolvedGamesFetching && !resolvedGames)
  // A failed request with no earlier payload is not "no games": say so and offer a retry.
  const showLoadError = !showSkeleton && !isPcConsoleTab && Boolean(isError) && !gameBlueprints

  // The header sticks to the page, which only works while no ancestor scrolls. Below the table's
  // minimum width the container has to scroll sideways and becomes the header's scroll container, so the
  // header is left unpinned there and scrolls with the rows.
  const [tableContainerRef, isHorizontallyScrolling] = useIsNarrowerThan<HTMLDivElement>(DRILLDOWN_TABLE_MIN_WIDTH)

  return (
    // `overflow: clip` (not MUI's default `hidden`) still rounds the corners but, unlike `hidden`,
    // does not turn the card into a scroll container that would pin the sticky header to itself.
    <Card sx={{ overflow: 'clip' }}>
      {/* Horizontal padding matches the grid's gutter so the title, hint and pills
          line up with the rank column and the right-hand link rail. */}
      <CardContent sx={{ px: `${TABLE_GUTTER}px`, pb: 1.5, '&:last-child': { pb: 1.5 } }}>
        {/* Three columns on wide cards so the platform toggle sits in the true centre of the
            header: title left, toggle middle, "Show:" pills right (the outer columns share
            the remaining width equally). Stacks on narrow ones. */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1fr) auto minmax(0, 1fr)' },
            alignItems: 'center',
            justifyItems: { xs: 'start', md: 'stretch' },
            gap: 1.5,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" gutterBottom>
              {t('market-analytics:drilldown_title', { blueprint: blueprintName })}
            </Typography>
            {showLoadError ? null : (
              <Typography variant="body2" color="text.secondary">
                {showSkeleton ? <Skeleton variant="text" width={280} /> : t('market-analytics:drilldown_hint', { count: visibleGameGroups.length })}
              </Typography>
            )}
            {showPlatformTabs ? (
              // The line's slot stays reserved on the Mobile tab (1.66em is the caption line
              // height) so switching tabs doesn't make the card header jump.
              <Typography variant="caption" color="text.secondary" component="p" sx={{ fontStyle: 'italic', mt: 0.5, minHeight: '1.66em' }}>
                {isPcConsoleTab ? t('market-analytics:drilldown_pc_console_disclaimer') : null}
              </Typography>
            ) : null}
            {isPcConsoleTab && effectiveSubgenreIds.length > 0 ? (
              <Typography variant="body2" color="text.secondary">
                {t('market-analytics:drilldown_pc_console_segment_note')}
              </Typography>
            ) : null}
            {!showSkeleton && failedFilterMatchIds.length > 0 ? (
              <Stack
                data-testid="drilldown-filter-load-error"
                role="alert"
                direction="row"
                alignItems="center"
                columnGap={1.5}
                flexWrap="wrap"
                sx={{ mt: 0.5 }}
              >
                <Typography variant="body2" color="warning.main">
                  {t('market-analytics:drilldown_filter_load_error')}
                </Typography>
                <Button variant="text" size="small" onClick={retryFailedFilterMatches} sx={inlineTextButtonSx}>
                  {t('market-analytics:retry')}
                </Button>
              </Stack>
            ) : null}
          </Box>
          {showPlatformTabs ? (
            <ToggleButtonGroup
              value={platformTab}
              exclusive
              size="small"
              onChange={handlePlatformTabChange}
              aria-label={t('market-analytics:drilldown_platform_label')}
              sx={platformToggleGroupSx}
            >
              {PLATFORM_TABS.map((tab) => (
                <ToggleButton key={tab} value={tab}>
                  {t(PLATFORM_TAB_LABEL_KEYS[tab])}
                  {/* Count as a chip inside the button: translucent white on the selected
                        (primary) fill, a soft grey on the unselected one. */}
                  <Chip
                    size="small"
                    label={platformGameCounts[tab]}
                    sx={(theme) => ({
                      // The theme's Chip override adds a bottom/right margin and !important
                      // label padding; both would push the count off the label's centre line.
                      m: 0,
                      ml: 0.75,
                      height: 18,
                      '& .MuiChip-label': { px: '6px !important', py: '0 !important', fontSize: '0.6875rem', lineHeight: 1 },
                      ...(tab === platformTab
                        ? { bgcolor: alpha(theme.palette.common.white, 0.25), color: 'primary.contrastText' }
                        : { bgcolor: alpha(theme.palette.text.primary, 0.08), color: 'text.secondary' }),
                    })}
                  />
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          ) : null}
          {/* Keyed off the unfiltered set: a year that hides every game must not take
              the pills with it, or there is no way back to "All". Pinned to the last column
              so they stay right-aligned even when the toggle is absent. */}
          {!showSkeleton && scopedItems.length > 0 ? (
            <Box sx={{ gridColumn: { md: 3 }, justifySelf: { md: 'end' } }}>
              <BlueprintEventsFilterPills value={effectiveFilter} onChange={setFilter} years={years} />
            </Box>
          ) : null}
        </Box>
      </CardContent>
      <Divider />
      {showSkeleton ? (
        <BlueprintGamesDrilldownRowsSkeleton />
      ) : showLoadError ? (
        <Stack data-testid="drilldown-games-load-error" role="alert" alignItems="center" spacing={1} sx={{ py: 5 }}>
          <Typography variant="body2" color="warning.main">
            {t('market-analytics:drilldown_load_error')}
          </Typography>
          {onRetry ? (
            <Button variant="text" size="small" onClick={onRetry}>
              {t('market-analytics:retry')}
            </Button>
          ) : null}
        </Stack>
      ) : visibleGameGroups.length === 0 ? (
        <Stack alignItems="center" spacing={1} sx={{ py: 5 }}>
          <Typography variant="body2" color="text.secondary">
            {typeof effectiveFilter === 'number'
              ? t('market-analytics:drilldown_empty_year', { year: effectiveFilter })
              : t('market-analytics:drilldown_empty')}
          </Typography>
          {effectiveFilter !== 'all' ? (
            <Button variant="text" size="small" onClick={() => setFilter('all')}>
              {t('market-analytics:drilldown_clear_year')}
            </Button>
          ) : null}
        </Stack>
      ) : (
        <TableContainer component={Box} ref={tableContainerRef} sx={{ overflowX: isHorizontallyScrolling ? 'auto' : 'visible' }}>
          <Table
            size="small"
            aria-label={t('market-analytics:drilldown_title', { blueprint: blueprintName })}
            sx={[DRILLDOWN_TABLE_SX, drilldownStickyHeaderSx(HIERARCHY_BAR_STICKY_TOP + HIERARCHY_BAR_STICKY_HEIGHT, !isHorizontallyScrolling)]}
          >
            <colgroup>
              {DRILLDOWN_COLUMNS.map((column) => (
                <col key={column.key} style={column.width ? { width: column.width } : undefined} />
              ))}
            </colgroup>
            <TableHead>
              <TableRow sx={(theme) => ({ '& > th': { borderBottomColor: gameDividerColor(theme) } })}>
                {DRILLDOWN_COLUMNS.map((column, index) => {
                  const header = COLUMN_HEADERS[column.key]
                  const label = t(header.labelKey)
                  const isLast = index === DRILLDOWN_COLUMNS.length - 1
                  return (
                    <TableCell
                      key={column.key}
                      align={isLast ? 'right' : 'left'}
                      sortDirection={header.sortKey && sort.key === header.sortKey ? sort.order : false}
                      className={index === 0 ? DRILLDOWN_CELL_CLASS.rank : isLast ? DRILLDOWN_CELL_CLASS.actions : undefined}
                      sx={{ '& .MuiTableSortLabel-root:not(.Mui-active)': { color: 'inherit' } }}
                    >
                      {header.sortKey ? (
                        <SortLabel
                          active={sort.key === header.sortKey}
                          direction={sort.order === 'asc' ? SortOrder.ASC : SortOrder.DESC}
                          onClick={() => handleSort(header.sortKey!)}
                        >
                          {label}
                        </SortLabel>
                      ) : header.tooltipKey ? (
                        <GRTooltip content={t(header.tooltipKey)} styles={{ cursor: 'help', display: 'inline-flex' }}>
                          <span>{label}</span>
                        </GRTooltip>
                      ) : (
                        label
                      )}
                    </TableCell>
                  )
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {DRILLDOWN_SECTION_ORDER.map((sectionId) => {
                const groups = sections[sectionId]
                if (groups.length === 0) return null
                const collapsed = showSections && collapsedSections.has(sectionId)
                return (
                  <React.Fragment key={sectionId}>
                    {showSections ? (
                      <DrilldownSectionHeaderRow
                        sectionId={sectionId}
                        count={groups.length}
                        collapsed={collapsed}
                        controls={groups.map((group) => gameRowIdOf(group.gameId)).join(' ')}
                        onToggle={toggleSection}
                      />
                    ) : null}
                    {collapsed
                      ? null
                      : groups.map((group) => (
                          <DrilldownGameRows
                            key={group.gameId}
                            rowId={gameRowIdOf(group.gameId)}
                            blueprintId={blueprintId}
                            blueprintName={blueprintName}
                            eventTypeId={eventTypeId}
                            group={group}
                            expanded={expandedGameIds.has(group.gameId)}
                            onToggle={toggleGame}
                            trackedGame={trackedGameById.get(group.gameId)}
                            filter={effectiveFilter}
                            sort={sort}
                            now={now}
                            includePcConsole={detailOptions.includePcConsole}
                            onLockedGameClick={(game) => openLockedGameDialog(undefined, game)}
                          />
                        ))}
                  </React.Fragment>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {lockedGameDialog}
    </Card>
  )
}

export const BlueprintGamesDrilldownTable = React.memo(BlueprintGamesDrilldownTableComponent)
BlueprintGamesDrilldownTable.displayName = 'BlueprintGamesDrilldownTable'
